import type { DrawingKind } from "lightweight-charts-drawing"
import { Option, Schema } from "effect"
import { DRAWING_TOOL_GROUPS, type DrawingTool } from "./tools.ts"

// Favourite drawing tools (TradingView's ☆ in the tool menus): starred tools show on a favourites
// bar over the charts, one click to arm. Pure: toolFavorites.ts saves them, FavoriteTools draws them.

const ALL_TOOLS: ReadonlyArray<DrawingTool> = DRAWING_TOOL_GROUPS.flatMap((group) => group.tools)
const toolByKind = new Map<string, DrawingTool>(ALL_TOOLS.map((tool) => [tool.kind, tool]))

/** Adds or removes a tool, keeping the order they were starred in. */
export const toggleFavorite = (favorites: ReadonlyArray<DrawingKind>, kind: DrawingKind): ReadonlyArray<DrawingKind> =>
  favorites.includes(kind) ? favorites.filter((favorite) => favorite !== kind) : [...favorites, kind]

const decode = Schema.decodeUnknownOption(Schema.fromJsonString(Schema.Struct({ version: Schema.Literal(1), tools: Schema.Array(Schema.String) })))

/** Saved favourites, keeping only tools this build still has; null when missing or not one. */
export const decodeFavorites = (json: string | null): ReadonlyArray<DrawingKind> | null => {
  if (json === null) return null
  const decoded = Option.getOrNull(decode(json))
  return decoded === null ? null : decoded.tools.flatMap((kind) => {
    const tool = toolByKind.get(kind)
    return tool === undefined ? [] : [tool.kind]
  })
}

export const favoriteTools = (favorites: ReadonlyArray<DrawingKind>): ReadonlyArray<DrawingTool> =>
  favorites.flatMap((kind) => {
    const tool = toolByKind.get(kind)
    return tool === undefined ? [] : [tool]
  })

// --- the favourites bar's place -------------------------------------------------------------------

export interface BarPosition {
  readonly left: number
  readonly top: number
}

/** Keeps the bar (of `size`) wholly inside a `viewport`: a drag or a smaller window can't lose it. */
export const clampBarPosition = (
  position: BarPosition,
  size: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number }
): BarPosition => ({
  left: Math.round(Math.max(0, Math.min(position.left, viewport.width - size.width))),
  top: Math.round(Math.max(0, Math.min(position.top, viewport.height - size.height)))
})
